import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import Layout from "../components/Layout";
import CustomerSelect from "../components/CustomerSelect";
import SearchableSelect from "../components/SearchableSelect";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ShoppingCart, DownloadSimple, UploadSimple, Plus, Trash, PencilSimple, MagnifyingGlass, X, Check } from "@phosphor-icons/react";

import { API } from "../App";
import { exportWithHeader } from "../utils/excelExport";

function sfmt(v) { const r = parseFloat(v) || 0; return r === 0 ? '-' : r.toFixed(2); }
function ybg(v) { return (parseFloat(v) || 0) > 0 ? 'bg-[#FFFDE7]' : ''; }

export default function SalesRegister() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [addDialog, setAddDialog] = useState(false);
  const [importDialog, setImportDialog] = useState(false);
  const [importErrors, setImportErrors] = useState([]);
  const [importing, setImporting] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editData, setEditData] = useState({});
  const [form, setForm] = useState({ customer: "", part_no: "", part_name: "", drg_no: "", part_rate: "", ordered_qty: "", po_no: "", delivery_date: "" });
  const [tab, setTab] = useState("table");  // "table" | "chart"
  const now = new Date();
  const [chartMode, setChartMode] = useState("all");  // "all" | "month" | "customer"
  const [chartMonth, setChartMonth] = useState(now.getMonth() + 1);
  const [chartYear, setChartYear] = useState(now.getFullYear());
  const [chartCustomer, setChartCustomer] = useState("");
  const [chartData, setChartData] = useState([]);
  const [delAllOpen, setDelAllOpen] = useState(false);
  const [delAllPwd, setDelAllPwd] = useState("");
  // Registration filter — "all" shows everything, "unregistered" isolates
  // rows whose part_no isn't in Item Master (highlighted pink in the table).
  const [regFilter, setRegFilter] = useState("all");  // "all" | "unregistered"
  // Delivery-date bucket filter. Empty prepondDate = show only Carry Forward
  // + Current buckets (hide future PO rows). Setting prepondDate reveals
  // Prepond rows with delivery_date ≤ that date. Bucket chip filter narrows
  // further to one specific bucket.
  const [prepondDate, setPrepondDate] = useState("");
  const [bucketFilter, setBucketFilter] = useState("default"); // "default" | "carry_forward" | "current" | "prepond" | "all"
  const fileRef = useRef(null);

  const fetchData = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await axios.get(`${API}/sales-register`);
      setData(res.data.data || []);
    } catch { if (!silent) toast.error("Failed to load sales register"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Fetch chart data when chart tab opens (or controls change)
  useEffect(() => {
    if (tab !== "chart") return;
    const params = new URLSearchParams({ mode: chartMode });
    if (chartMode === "month") { params.append("month", chartMonth); params.append("year", chartYear); }
    if (chartMode === "monthly") { params.append("year", chartYear); }
    if (chartMode === "customer") { params.append("customer", chartCustomer); params.append("year", chartYear); }
    if (chartMode === "customer" && !chartCustomer) { setChartData([]); return; }
    axios.get(`${API}/sales-register/chart/customer-value?${params.toString()}`, { skipPeriod: true })
      .then(r => setChartData(r.data || []))
      .catch(() => toast.error("Failed to load chart data"));
  }, [tab, chartMode, chartMonth, chartYear, chartCustomer]);

  // Unique customer list for the dropdown, derived from loaded table data.
  const customerList = Array.from(new Set((data || []).map(r => r.customer).filter(Boolean))).sort();

  const handleDeleteAll = async () => {
    if (!delAllPwd.trim()) { toast.error("Password required"); return; }
    try {
      const res = await axios.delete(`${API}/sales-register/all?password=${encodeURIComponent(delAllPwd)}`);
      toast.success(res.data.message || "All entries deleted");
      setDelAllOpen(false);
      setDelAllPwd("");
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete");
    }
  };

  // Auto-refresh every 1 second (silent)
  useEffect(() => {
    const interval = setInterval(() => fetchData(true), 20000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleAdd = async () => {
    if (!form.part_no || !form.customer) { toast.error("Customer & Part No required"); return; }
    try {
      await axios.post(`${API}/sales-register`, form);
      toast.success("Entry added");
      setAddDialog(false);
      setForm({ customer: "", part_no: "", part_name: "", drg_no: "", part_rate: "", ordered_qty: "", po_no: "", delivery_date: "" });
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to add"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this entry?")) return;
    try { await axios.delete(`${API}/sales-register/${id}`); toast.success("Deleted"); fetchData(); }
    catch { toast.error("Failed to delete"); }
  };

  const startEdit = (entry) => {
    setEditId(entry.id);
    setEditData({ customer: entry.customer, part_no: entry.part_no, part_name: entry.part_name, drg_no: entry.drg_no, part_rate: entry.part_rate, ordered_qty: entry.ordered_qty, po_no: entry.po_no, delivery_date: entry.delivery_date });
  };

  const saveEdit = async () => {
    try {
      await axios.put(`${API}/sales-register/${editId}`, editData);
      toast.success("Updated");
      setEditId(null);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to update"); }
  };

  const handleDownloadTemplate = async () => {
    try {
      const res = await axios.get(`${API}/sales-register/template`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url;
      a.download = "sales_register_template.xlsx"; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImporting(true); setImportErrors([]);
    try {
      const fd = new FormData(); fd.append("file", file);
      const res = await axios.post(`${API}/sales-register/import`, fd);
      if (res.data.errors?.length) {
        setImportErrors(res.data.errors);
        toast.warning(`Imported ${res.data.saved} with ${res.data.errors.length} errors`);
      } else {
        toast.success(`Imported ${res.data.saved} entries`);
        setImportDialog(false);
      }
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Import failed"); }
    finally { setImporting(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const sq = search.toLowerCase().trim();

  // ── Delivery-date bucket logic ──────────────────────────────────
  // Reference month = current calendar month (today). "Carry Forward" =
  // rows whose delivery_date is before this month AND still have qty
  // pending (balance_qty > 0). "Current" = rows delivered THIS month.
  // "Prepond" = rows delivered AFTER this month (future POs).
  const _today = new Date();
  const _monthStart = `${_today.getFullYear()}-${String(_today.getMonth() + 1).padStart(2, "0")}-01`;
  const _monthPrefix = `${_today.getFullYear()}-${String(_today.getMonth() + 1).padStart(2, "0")}`;
  const _computeBucket = (r) => {
    const dd = (r.delivery_date || "").trim();
    if (!dd) return "other";
    if (dd < _monthStart) return (r.balance_qty || 0) > 0 ? "carry_forward" : "other";
    if (dd.startsWith(_monthPrefix)) return "current";
    return "prepond";
  };
  const dataWithBuckets = (data || []).map(r => ({ ...r, __bucket: _computeBucket(r) }));

  // Bucket counts across ALL rows (regardless of other filters) — for chips.
  const bucketCounts = dataWithBuckets.reduce((acc, r) => {
    acc[r.__bucket] = (acc[r.__bucket] || 0) + 1;
    return acc;
  }, {});
  // Effective Prepond count = respects prepondDate cutoff so the chip shows
  // exactly how many future POs will surface when Prepond mode is active.
  const prepondEffectiveCount = dataWithBuckets.filter(r =>
    r.__bucket === "prepond" && (!prepondDate || (r.delivery_date || "") <= prepondDate)
  ).length;

  // Prepond visibility: default mode HIDES prepond rows unless the user has
  // set a prepondDate — in which case prepond rows with delivery_date ≤ that
  // date are shown. Explicit chip selection overrides everything.
  const bucketFiltered = dataWithBuckets.filter(r => {
    if (bucketFilter === "all") return true;
    if (bucketFilter === "carry_forward") return r.__bucket === "carry_forward";
    if (bucketFilter === "current") return r.__bucket === "current";
    if (bucketFilter === "prepond") {
      if (r.__bucket !== "prepond") return false;
      if (!prepondDate) return true;
      return (r.delivery_date || "") <= prepondDate;
    }
    // "default" — CF + Current always; Prepond only when prepondDate set
    if (r.__bucket === "carry_forward" || r.__bucket === "current") return true;
    if (r.__bucket === "prepond" && prepondDate) return (r.delivery_date || "") <= prepondDate;
    // Rows without a delivery_date fall through — keep them visible so the
    // register isn't accidentally emptied by the filter.
    if (r.__bucket === "other") return true;
    return false;
  });

  // Apply the registration filter FIRST — then the free-text search runs
  // on the reduced set. Both totals below reflect the visible subset.
  const regFiltered = regFilter === "unregistered"
    ? bucketFiltered.filter(r => r.unregistered === true || !r.part_id)
    : bucketFiltered;
  const filtered = sq ? regFiltered.filter(r =>
    (r.customer || "").toLowerCase().includes(sq) ||
    (r.part_no || "").toLowerCase().includes(sq) ||
    (r.part_name || "").toLowerCase().includes(sq) ||
    (r.po_no || "").toLowerCase().includes(sq)
  ) : regFiltered;

  const unregCount = data.filter(r => r.unregistered === true || !r.part_id).length;

  const totalOrdered = filtered.reduce((s, r) => s + (r.ordered_qty || 0), 0);
  const totalOrderValue = filtered.reduce((s, r) => s + ((r.ordered_qty || 0) * (r.part_rate || 0)), 0);
  const totalDispatched = filtered.reduce((s, r) => s + (r.dispatch_qty || 0), 0);
  const totalBalance = filtered.reduce((s, r) => s + (r.balance_qty || 0), 0);

  // sfmt for compact CR/L display when totals get large (order value totals).
  function crFmt(v) {
    const n = parseFloat(v) || 0;
    if (n >= 1e7) return `₹${(n / 1e7).toFixed(2)} CR`;
    if (n >= 1e5) return `₹${(n / 1e5).toFixed(2)} L`;
    return `₹${n.toFixed(2)}`;
  }

  // Defining the field component inside this function caused the input to
  // unmount/remount on every keystroke (new component identity each render),
  // which dropped focus after typing each character. Inline the field JSX
  // in the dialog instead. Keeping the helper as a stable factory below
  // means the same JSX tree is reused across renders.
  const renderField = (label, field, type = "text") => (
    <div key={field}>
      <Label className="text-xs font-semibold">{label}</Label>
      <Input type={type} value={form[field]} onChange={e => setForm(p => ({ ...p, [field]: e.target.value }))}
        className="min-h-[40px] text-sm mt-0.5" data-testid={`add-${field}`} />
    </div>
  );

  return (
    <Layout>
      <div className="space-y-3 p-4 max-w-full" data-testid="sales-register-page">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <ShoppingCart size={22} weight="bold" className="text-[#1D2951]" />
            <h1 className="text-lg font-bold text-[#1D2951]" style={{ fontFamily: 'Chivo' }} data-testid="sales-register-title">Sales Register</h1>
            <span className="text-xs text-[#64748B] font-bold ml-2">{filtered.length} orders</span>
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            <Button onClick={() => setAddDialog(true)} size="sm"
              className="bg-[#059669] hover:bg-[#047857] text-white min-h-[30px] text-xs" data-testid="add-entry-btn">
              <Plus size={13} className="mr-1" /> Add Entry
            </Button>
            <Button variant="outline" size="sm" onClick={handleDownloadTemplate}
              className="min-h-[30px] text-xs text-[#1D2951]" data-testid="download-template-btn">
              <DownloadSimple size={13} className="mr-1" /> Template
            </Button>
            <Button variant="outline" size="sm" onClick={() => setImportDialog(true)}
              className="min-h-[30px] text-xs text-[#1D2951]" data-testid="import-btn">
              <UploadSimple size={13} className="mr-1" /> Import
            </Button>
            <Button size="sm" onClick={() => {
              const rows = filtered.map(r => ({
                "Status": (r.unregistered === true || !r.part_id) ? "NOT IN ITEM MASTER" : "OK",
                "Customer": r.customer || "",
                "Part No": r.part_no || "",
                "Part Name": r.part_name || "",
                "Drg No": r.drg_no || "",
                "Part Rate": r.part_rate || 0,
                "Ordered Quantity": r.ordered_qty || 0,
                "Dispatch Quantity": r.dispatch_qty || 0,
                "Balance Quantity": r.balance_qty || 0,
                "PO No": r.po_no || "",
                "Delivery Date": r.delivery_date || "",
              }));
              const sheetName = regFilter === "unregistered" ? "Not In Item Master" : "Sales Register";
              exportWithHeader([{ name: sheetName, data: rows }],
                `${sheetName.replace(/ /g, "_")}_${new Date().toISOString().slice(0,10)}`, sheetName);
              toast.success(`Exported ${rows.length} row(s)!`);
            }} className="bg-[#1D2951] hover:bg-[#2A3B73] text-white min-h-[30px] text-xs" data-testid="export-sales-btn">
              <DownloadSimple size={13} className="mr-1" /> Export Excel
            </Button>
            <Button variant="outline" size="sm" onClick={async () => {
              if (!window.confirm("Scan for duplicate rows (same Customer + Part + Purchase Order + Delivery Date + Quantity + Rate) and keep only the first of each group?")) return;
              try {
                const r = await axios.post(`${API}/sales-register/dedupe`);
                toast.success(r.data.message);
                fetchData();
              } catch (err) { toast.error(err.response?.data?.detail || "Dedupe failed"); }
            }}
              className="min-h-[30px] text-xs text-[#B45309] border-[#F59E0B] hover:bg-[#FEF3C7]"
              data-testid="dedupe-btn"
              title="One-shot cleanup: removes duplicate rows caused by a bad import (kept the first of each group).">
              <Check size={13} className="mr-1" /> Dedupe
            </Button>
            <Button variant="outline" size="sm" onClick={() => setDelAllOpen(true)}
              className="min-h-[30px] text-xs text-[#DC2626] border-[#DC2626] hover:bg-[#DC2626] hover:text-white"
              data-testid="delete-all-btn">
              <Trash size={13} className="mr-1" /> Delete All
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-[#E2E8F0]" data-testid="sales-tabs">
          <button onClick={() => setTab("table")} data-testid="tab-table"
            className={`px-4 py-2 text-xs font-bold uppercase tracking-wide rounded-t ${tab === "table" ? "bg-[#1D2951] text-[#F5DF4D]" : "bg-[#F1F5F9] text-[#475569]"}`}>
            Table
          </button>
          <button onClick={() => setTab("chart")} data-testid="tab-chart"
            className={`px-4 py-2 text-xs font-bold uppercase tracking-wide rounded-t ${tab === "chart" ? "bg-[#1D2951] text-[#F5DF4D]" : "bg-[#F1F5F9] text-[#475569]"}`}>
            Chart — Customer × Order Value
          </button>
        </div>

        {tab === "chart" ? (
          <>
            {/* Chart Toolbar */}
            <Card className="p-2 border border-[#E2E8F0]">
              <div className="flex gap-3 items-end flex-wrap">
                <div className="flex items-end gap-1">
                  <Button size="sm" variant={chartMode === "all" ? "default" : "outline"}
                    onClick={() => { setChartMode("all"); setChartCustomer(""); }}
                    className={`min-h-[32px] text-xs ${chartMode === "all" ? 'bg-[#1D2951] text-white' : 'text-[#1D2951]'}`}
                    data-testid="chart-mode-all">All</Button>
                  <Button size="sm" variant={chartMode === "month" ? "default" : "outline"}
                    onClick={() => { setChartMode("month"); setChartCustomer(""); }}
                    className={`min-h-[32px] text-xs ${chartMode === "month" ? 'bg-[#059669] text-white' : 'text-[#059669] border-[#059669]'}`}
                    data-testid="chart-mode-month">Month</Button>
                  <Button size="sm" variant={chartMode === "monthly" ? "default" : "outline"}
                    onClick={() => { setChartMode("monthly"); setChartCustomer(""); }}
                    className={`min-h-[32px] text-xs ${chartMode === "monthly" ? 'bg-[#7C3AED] text-white' : 'text-[#7C3AED] border-[#7C3AED]'}`}
                    title="All months · all customers grouped per month"
                    data-testid="chart-mode-monthly">Monthly</Button>
                </div>
                {(chartMode === "month" || chartMode === "monthly") && (
                  <>
                    {chartMode === "month" && (
                      <div className="w-[100px]">
                        <Label className="text-[11px] font-semibold block mb-0.5">Month</Label>
                        <select value={chartMonth} onChange={e => setChartMonth(parseInt(e.target.value))}
                          className="w-full h-[32px] px-2 text-xs border rounded border-[#CBD5E1]" data-testid="chart-month">
                          {["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"].map((l,i) =>
                            <option key={i+1} value={i+1}>{l}</option>)}
                        </select>
                      </div>
                    )}
                    <div className="w-[90px]">
                      <Label className="text-[11px] font-semibold block mb-0.5">Year</Label>
                      <select value={chartYear} onChange={e => setChartYear(parseInt(e.target.value))}
                        className="w-full h-[32px] px-2 text-xs border rounded border-[#CBD5E1]" data-testid="chart-year">
                        {[now.getFullYear()-1, now.getFullYear(), now.getFullYear()+1].map(y => <option key={y} value={y}>{y}</option>)}
                      </select>
                    </div>
                  </>
                )}
                <div className="border-l border-[#CBD5E1] pl-3 flex items-end gap-2">
                  <div className="w-[240px]">
                    <Label className="text-[11px] font-semibold block mb-0.5 text-[#7C3AED]">Customer (month-wise view)</Label>
                    <SearchableSelect
                      value={chartCustomer}
                      onChange={(v) => { setChartCustomer(v); if (v) setChartMode("customer"); }}
                      options={customerList.map(c => ({ value: c, label: c }))}
                      placeholder="None (per-customer view)"
                      size="sm"
                      testId="chart-customer"
                    />
                  </div>
                  {chartMode === "customer" && (
                    <div className="w-[90px]">
                      <Label className="text-[11px] font-semibold block mb-0.5">Year</Label>
                      <select value={chartYear} onChange={e => setChartYear(parseInt(e.target.value))}
                        className="w-full h-[32px] px-2 text-xs border rounded border-[#CBD5E1]" data-testid="chart-customer-year">
                        {[now.getFullYear()-1, now.getFullYear(), now.getFullYear()+1].map(y => <option key={y} value={y}>{y}</option>)}
                      </select>
                    </div>
                  )}
                </div>
              </div>
            </Card>
            <CustomerChart rows={chartData} mode={chartMode} month={chartMonth} year={chartYear} customer={chartCustomer} />
          </>
        ) : (<></>)}

        {tab === "table" && (<>
        {/* Search + Summary */}
        {/* Filter chips + Search */}
        <Card className="p-2 border border-[#E2E8F0]">
          <div className="flex gap-3 items-end flex-wrap">
            <div>
              <Label className="text-[11px] font-semibold block mb-0.5">Delivery</Label>
              <div className="flex gap-1 flex-wrap">
                <Button size="sm"
                  variant={bucketFilter === "default" ? "default" : "outline"}
                  onClick={() => setBucketFilter("default")}
                  className={`min-h-[30px] text-xs ${bucketFilter === "default" ? 'bg-[#0EA5E9] text-white hover:bg-[#0284C7]' : 'text-[#0EA5E9] border-[#93C5FD]'}`}
                  title={prepondDate ? `Carry-Forward + Current + Prepond ≤ ${prepondDate}` : "Carry-Forward + Current (Prepond hidden until date set)"}
                  data-testid="bucket-filter-default"
                >Default</Button>
                <Button size="sm"
                  variant={bucketFilter === "carry_forward" ? "default" : "outline"}
                  onClick={() => setBucketFilter("carry_forward")}
                  className={`min-h-[30px] text-xs ${bucketFilter === "carry_forward" ? 'bg-[#DC2626] text-white hover:bg-[#B91C1C]' : 'text-[#DC2626] border-[#FCA5A5]'}`}
                  title="POs with delivery_date before this month (overdue balance)"
                  data-testid="bucket-filter-cf"
                >C/F ({bucketCounts.carry_forward || 0})</Button>
                <Button size="sm"
                  variant={bucketFilter === "current" ? "default" : "outline"}
                  onClick={() => setBucketFilter("current")}
                  className={`min-h-[30px] text-xs ${bucketFilter === "current" ? 'bg-[#059669] text-white hover:bg-[#047857]' : 'text-[#059669] border-[#6EE7B7]'}`}
                  title="POs due within this calendar month"
                  data-testid="bucket-filter-current"
                >Current ({bucketCounts.current || 0})</Button>
                <Button size="sm"
                  variant={bucketFilter === "prepond" ? "default" : "outline"}
                  onClick={() => setBucketFilter("prepond")}
                  className={`min-h-[30px] text-xs ${bucketFilter === "prepond" ? 'bg-[#7C3AED] text-white hover:bg-[#6D28D9]' : 'text-[#7C3AED] border-[#C4B5FD]'}`}
                  title="Future POs — filtered by Prepond Date if set"
                  data-testid="bucket-filter-prepond"
                >Prepond ({prepondDate ? `${prepondEffectiveCount}/${bucketCounts.prepond || 0}` : (bucketCounts.prepond || 0)})</Button>
                <Button size="sm"
                  variant={bucketFilter === "all" ? "default" : "outline"}
                  onClick={() => setBucketFilter("all")}
                  className={`min-h-[30px] text-xs ${bucketFilter === "all" ? 'bg-[#1D2951] text-white hover:bg-[#2A3B73]' : 'text-[#1D2951] border-[#CBD5E1]'}`}
                  data-testid="bucket-filter-all"
                >All</Button>
              </div>
            </div>
            <div>
              <Label className="text-[11px] font-semibold block mb-0.5">Prepond Up To</Label>
              <div className="flex gap-1 items-center">
                <Input
                  type="date"
                  value={prepondDate}
                  onChange={e => setPrepondDate(e.target.value)}
                  className="min-h-[30px] text-xs w-[145px]"
                  data-testid="prepond-date-input"
                  title="Set a date to include Prepond (future) POs with delivery_date ≤ this. Leave blank to hide Prepond."
                />
                {prepondDate && (
                  <Button size="sm" variant="ghost" onClick={() => setPrepondDate("")}
                    className="min-h-[30px] text-xs px-2 text-[#DC2626]"
                    data-testid="prepond-clear">Clear</Button>
                )}
              </div>
            </div>
            <div>
              <Label className="text-[11px] font-semibold block mb-0.5">Registration</Label>
              <div className="flex gap-1">
                <Button size="sm"
                  variant={regFilter === "all" ? "default" : "outline"}
                  onClick={() => setRegFilter("all")}
                  className={`min-h-[30px] text-xs ${regFilter === "all" ? 'bg-[#1D2951] text-white hover:bg-[#2A3B73]' : 'text-[#1D2951] border-[#CBD5E1]'}`}
                  data-testid="reg-filter-all"
                >All ({data.length})</Button>
                <Button size="sm"
                  variant={regFilter === "unregistered" ? "default" : "outline"}
                  onClick={() => setRegFilter("unregistered")}
                  className={`min-h-[30px] text-xs ${regFilter === "unregistered" ? 'bg-[#DC2626] text-white hover:bg-[#B91C1C]' : 'text-[#DC2626] border-[#FCA5A5]'}`}
                  data-testid="reg-filter-unreg"
                  title="PO rows whose Part No isn't registered in Item Master yet"
                >Not in Item Master ({unregCount})</Button>
              </div>
            </div>
            <div className="w-[220px]">
              <Label className="text-[11px] font-semibold block mb-0.5">Search</Label>
              <div className="relative">
                <MagnifyingGlass size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
                <Input placeholder="Customer / Part / PO..." value={search} onChange={e => setSearch(e.target.value)}
                  className="pl-7 min-h-[30px] text-xs" data-testid="sales-search" />
              </div>
            </div>
            <div className="flex gap-4 ml-auto text-xs font-bold items-center flex-wrap">
              <span className="text-[#1D2951]">Ordered: {sfmt(totalOrdered)}</span>
              <span
                className="text-[#7C3AED]"
                title="Total value of every PO row currently loaded in the register."
                data-testid="sr-order-value-all"
              >Order Value: {crFmt(totalOrderValue)}</span>
              <span className="text-[#059669]">Dispatched: {sfmt(totalDispatched)}</span>
              <span className="text-[#DC2626]">Balance: {sfmt(totalBalance)}</span>
            </div>
          </div>
        </Card>

        {/* Table */}
        {loading ? (
          <div className="text-center py-12 text-[#64748B]">Loading...</div>
        ) : (
          <Card className="border border-[#E2E8F0] overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse" data-testid="sales-register-table">
                <thead>
                  <tr className="bg-[#1D2951] text-white">
                    <th className="px-2 py-2 text-xs font-bold text-left w-[40px]">Sr.No</th>
                    <th className="px-2 py-2 text-xs font-bold text-left min-w-[120px]">Customer Name</th>
                    <th className="px-2 py-2 text-xs font-bold text-left min-w-[100px]">Part No.</th>
                    <th className="px-2 py-2 text-xs font-bold text-left min-w-[100px]">Part Name</th>
                    <th className="px-2 py-2 text-xs font-bold text-left min-w-[80px]">Drg No.</th>
                    <th className="px-2 py-2 text-xs font-bold text-right min-w-[70px]">Part Rate</th>
                    <th className="px-2 py-2 text-xs font-bold text-right min-w-[70px]">Ordered Quantity</th>
                    <th className="px-2 py-2 text-xs font-bold text-right min-w-[80px] bg-[#7C3AED]">Order Value</th>
                    <th className="px-2 py-2 text-xs font-bold text-left min-w-[90px]">PO No.</th>
                    <th className="px-2 py-2 text-xs font-bold text-left min-w-[90px]">Delivery Date</th>
                    <th className="px-2 py-2 text-xs font-bold text-right min-w-[80px] bg-[#059669]">Dispatch Quantity</th>
                    <th className="px-2 py-2 text-xs font-bold text-right min-w-[80px] bg-[#DC2626]">Balance Quantity</th>
                    <th className="px-2 py-2 text-xs font-bold text-center w-[70px]">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white">
                  {filtered.map(r => {
                    const isEditing = editId === r.id;
                    const orderValue = (r.ordered_qty || 0) * (r.part_rate || 0);
                    if (isEditing) {
                      const editOrderValue = (parseFloat(editData.ordered_qty) || 0) * (parseFloat(editData.part_rate) || 0);
                      return (
                        <tr key={r.id} className="bg-[#FFFBEB] border-b border-[#E2E8F0]" data-testid={`edit-row-${r.sr}`}>
                          <td className="px-2 py-1 text-xs font-bold">{r.sr}</td>
                          <td className="px-1 py-0.5"><Input className="h-7 text-xs" value={editData.customer} onChange={e => setEditData(p => ({ ...p, customer: e.target.value }))} /></td>
                          <td className="px-1 py-0.5"><Input className="h-7 text-xs" value={editData.part_no} onChange={e => setEditData(p => ({ ...p, part_no: e.target.value }))} /></td>
                          <td className="px-1 py-0.5"><Input className="h-7 text-xs" value={editData.part_name} onChange={e => setEditData(p => ({ ...p, part_name: e.target.value }))} /></td>
                          <td className="px-1 py-0.5"><Input className="h-7 text-xs" value={editData.drg_no} onChange={e => setEditData(p => ({ ...p, drg_no: e.target.value }))} /></td>
                          <td className="px-1 py-0.5"><Input type="number" className="h-7 text-xs text-right" value={editData.part_rate} onChange={e => setEditData(p => ({ ...p, part_rate: e.target.value }))} /></td>
                          <td className="px-1 py-0.5"><Input type="number" className="h-7 text-xs text-right" value={editData.ordered_qty} onChange={e => setEditData(p => ({ ...p, ordered_qty: e.target.value }))} /></td>
                          <td className="px-2 py-1 text-right text-xs font-mono font-bold text-[#7C3AED]">{editOrderValue.toFixed(2)}</td>
                          <td className="px-1 py-0.5"><Input className="h-7 text-xs" value={editData.po_no} onChange={e => setEditData(p => ({ ...p, po_no: e.target.value }))} /></td>
                          <td className="px-1 py-0.5"><Input type="date" className="h-7 text-xs" value={editData.delivery_date} onChange={e => setEditData(p => ({ ...p, delivery_date: e.target.value }))} /></td>
                          <td className="px-2 py-1 text-right text-xs font-mono font-bold text-[#059669]">{(r.dispatch_qty || 0).toFixed(2)}</td>
                          <td className="px-2 py-1 text-right text-xs font-mono font-bold text-[#DC2626]">{(r.balance_qty || 0).toFixed(2)}</td>
                          <td className="px-1 py-1 text-center">
                            <div className="flex gap-0.5 justify-center">
                              <Button variant="ghost" size="sm" onClick={saveEdit} className="h-6 w-6 p-0 text-[#059669]" data-testid="save-edit"><Check size={12} /></Button>
                              <Button variant="ghost" size="sm" onClick={() => setEditId(null)} className="h-6 w-6 p-0 text-[#64748B]" data-testid="cancel-edit"><X size={12} /></Button>
                            </div>
                          </td>
                        </tr>
                      );
                    }
                    return (
                      <tr key={r.id} className={`border-b border-[#E2E8F0] ${r.unregistered ? 'bg-[#FCE7F3] hover:bg-[#FBCFE8]' : 'hover:bg-[#F8FAFC]'}`} data-testid={`sales-row-${r.sr}`}>
                        <td className="px-2 py-2 text-xs font-bold text-[#94A3B8]">{r.sr}</td>
                        <td className="px-2 py-2 text-xs font-bold text-[#1D2951]">{r.customer}</td>
                        <td className="px-2 py-2 text-xs font-bold">
                          {r.part_no}
                          {r.unregistered && <span title="Part No not in Item Master — add it there to clear this flag" className="ml-1 text-[9px] font-bold text-[#9D174D] bg-[#FBCFE8] rounded px-1 py-[1px] align-middle" data-testid={`unregistered-badge-${r.sr}`}>NOT IN MASTER</span>}
                        </td>
                        <td className="px-2 py-2 text-xs">{r.part_name}</td>
                        <td className="px-2 py-2 text-xs font-mono">{r.drg_no}</td>
                        <td className={`px-2 py-2 text-xs text-right font-mono ${ybg(r.part_rate)}`}>{sfmt(r.part_rate)}</td>
                        <td className={`px-2 py-2 text-xs text-right font-mono font-bold ${ybg(r.ordered_qty)}`}>{sfmt(r.ordered_qty)}</td>
                        <td className={`px-2 py-2 text-right text-xs font-mono font-bold text-[#7C3AED] ${ybg(orderValue)}`}>{sfmt(orderValue)}</td>
                        <td className="px-2 py-2 text-xs font-mono">{r.po_no}</td>
                        <td className="px-2 py-2 text-xs font-mono">
                          <div className="flex items-center gap-1">
                            <span>{r.delivery_date || '-'}</span>
                            {r.__bucket === "carry_forward" && (
                              <span className="text-[9px] font-bold px-1 py-0.5 rounded bg-[#FEE2E2] text-[#DC2626] border border-[#FCA5A5]" title="Carry Forward — delivery date before this month, balance still open">C/F</span>
                            )}
                            {r.__bucket === "current" && (
                              <span className="text-[9px] font-bold px-1 py-0.5 rounded bg-[#DCFCE7] text-[#059669] border border-[#6EE7B7]" title="Delivery within this month">CUR</span>
                            )}
                            {r.__bucket === "prepond" && (
                              <span className="text-[9px] font-bold px-1 py-0.5 rounded bg-[#EDE9FE] text-[#7C3AED] border border-[#C4B5FD]" title="Future PO — prepond bucket">PREPOND</span>
                            )}
                          </div>
                        </td>
                        <td className={`px-2 py-2 text-right text-xs font-mono font-bold text-[#059669] ${ybg(r.dispatch_qty)}`}>{sfmt(r.dispatch_qty)}</td>
                        <td className={`px-2 py-2 text-right text-xs font-mono font-bold ${r.balance_qty > 0 ? 'text-[#DC2626] bg-[#FFFDE7]' : 'text-[#059669]'}`}>{sfmt(r.balance_qty)}</td>
                        <td className="px-2 py-2 text-center">
                          <div className="flex gap-0.5 justify-center">
                            <Button variant="ghost" size="sm" onClick={() => startEdit(r)} className="h-6 w-6 p-0 text-[#3B82F6]" data-testid={`edit-${r.sr}`}><PencilSimple size={12} /></Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDelete(r.id)} className="h-6 w-6 p-0 text-[#EF4444]" data-testid={`delete-${r.sr}`}><Trash size={12} /></Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {filtered.length === 0 && !loading && (
                <div className="text-center py-10 text-[#64748B] text-sm">No sales entries found. Add manually or import from Excel.</div>
              )}
            </div>
          </Card>
        )}
        </>)}

        {/* Add Entry Dialog */}
        <Dialog open={addDialog} onOpenChange={setAddDialog}>
          <DialogContent className="max-w-lg" data-testid="add-entry-dialog">
            <DialogHeader><DialogTitle>Add Sales Entry</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div key="customer">
                <Label className="text-xs font-semibold">Customer Name *</Label>
                <CustomerSelect value={form.customer}
                  onChange={(v) => setForm(p => ({ ...p, customer: v }))}
                  testId="add-customer" />
              </div>
              {renderField("Part No *", "part_no")}
              {renderField("Part Name", "part_name")}
              {renderField("Drg No", "drg_no")}
              {renderField("Part Rate", "part_rate", "number")}
              {renderField("Ordered Quantity *", "ordered_qty", "number")}
              {renderField("PO No", "po_no")}
              {renderField("Delivery Date", "delivery_date", "date")}
            </div>
            <Button onClick={handleAdd} className="w-full bg-[#1D2951] hover:bg-[#2A3B73] text-white min-h-[44px] mt-2" data-testid="confirm-add-btn">
              <Plus size={16} className="mr-2" /> Add Entry
            </Button>
          </DialogContent>
        </Dialog>

        {/* Import Dialog */}
        <Dialog open={importDialog} onOpenChange={setImportDialog}>
          <DialogContent className="max-w-md" data-testid="import-dialog">
            <DialogHeader><DialogTitle>Import Sales Register</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <p className="text-xs text-[#64748B]">Upload .xlsx file (use downloaded template). Part No must exist in Item Master.</p>
              <input ref={fileRef} type="file" accept=".xlsx" onChange={handleImport} className="text-sm" data-testid="import-file-input" />
              {importing && <p className="text-xs text-[#2563EB]">Importing...</p>}
              {importErrors.length > 0 && (
                <div className="max-h-[200px] overflow-y-auto bg-[#FEF2F2] rounded p-2 space-y-1">
                  <p className="text-xs font-bold text-[#991B1B]">Errors ({importErrors.length}):</p>
                  {importErrors.map((e, i) => <p key={i} className="text-xs text-[#991B1B]">Row {e.row}: {e.error}</p>)}
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Delete-All Dialog */}
        <Dialog open={delAllOpen} onOpenChange={(o) => { setDelAllOpen(o); if (!o) setDelAllPwd(""); }}>
          <DialogContent className="max-w-sm" data-testid="delete-all-dialog">
            <DialogHeader><DialogTitle>Delete ALL Sales Entries</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <p className="text-xs text-[#DC2626] font-bold">⚠ This will permanently delete every entry in this register. This cannot be undone.</p>
              <div>
                <Label className="text-[11px] font-semibold">Confirmation Password</Label>
                <Input type="password" value={delAllPwd} onChange={e => setDelAllPwd(e.target.value)}
                  placeholder="Enter CEO password" className="min-h-[40px]" data-testid="delete-all-password" autoFocus
                  onKeyDown={(e) => e.key === "Enter" && handleDeleteAll()} />
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setDelAllOpen(false)} className="flex-1 min-h-[40px]">
                  Cancel
                </Button>
                <Button onClick={handleDeleteAll} className="flex-1 min-h-[40px] bg-[#DC2626] hover:bg-[#B91C1C] text-white" data-testid="confirm-delete-all">
                  <Trash size={14} className="mr-1" /> Delete All
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}


function CustomerChart({ rows, mode = "all", month, year, customer }) {
  // ────────────────── Monthly grouped-bar mode ──────────────────
  // X-axis = each month; inside each month group, one mini-bar per customer.
  if (mode === "monthly") {
    if (!rows || rows.length === 0) {
      return (
        <Card className="p-10 border border-[#E2E8F0] text-center text-sm text-[#64748B]" data-testid="chart-empty">
          No data for {year}.
        </Card>
      );
    }
    // Build a stable color per customer (so the same customer has the same color in every month group)
    const palette = ['#1D2951','#7C3AED','#059669','#DC2626','#F59E0B','#0EA5E9','#EC4899','#10B981','#6366F1','#F97316','#8B5CF6','#14B8A6'];
    const customerSet = new Set();
    rows.forEach(r => (r.customers || []).forEach(c => customerSet.add(c.name)));
    const customers = Array.from(customerSet).sort();
    const colorOf = Object.fromEntries(customers.map((c, i) => [c, palette[i % palette.length]]));

    const max = Math.max(1, ...rows.flatMap(r => (r.customers || []).map(c => c.total_value || 0)));
    const yearTotal = rows.reduce((s, r) => s + (r.month_total_value || 0), 0);
    const fmtINR = (v) => {
      const n = v || 0;
      if (n >= 10000000) return `₹${(n / 10000000).toFixed(2)}Cr`;
      if (n >= 100000)   return `₹${(n / 100000).toFixed(2)}L`;
      if (n >= 1000)     return `₹${(n / 1000).toFixed(1)}K`;
      return `₹${n.toFixed(0)}`;
    };
    const fmtFull = (v) => `₹${(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
    const CHART_H = 280;
    // Bar width adjusts so the chart fits horizontally without crushing
    const BAR_W = customers.length <= 4 ? 22 : customers.length <= 8 ? 16 : 12;
    const GROUP_GAP = 28;

    return (
      <Card className="border border-[#E2E8F0] overflow-hidden" data-testid="customer-chart">
        <div className="bg-[#7C3AED] px-5 py-3 flex items-center gap-2">
          <ShoppingCart size={18} className="text-white" />
          <h2 className="text-sm font-bold text-white uppercase tracking-wider">
            Monthly Order Value · All Customers · {year}
          </h2>
          <span className="ml-auto text-[11px] text-white/80">
            {customers.length} customer(s) · Year Total {fmtFull(yearTotal)}
          </span>
        </div>

        {/* Color legend */}
        <div className="px-5 pt-3 flex flex-wrap gap-2 text-[11px]">
          {customers.map(c => (
            <span key={c} className="flex items-center gap-1 bg-[#F8FAFC] border border-[#E2E8F0] rounded-full px-2 py-0.5">
              <span className="w-2.5 h-2.5 rounded-sm" style={{ background: colorOf[c] }} />
              <span className="font-semibold text-[#1D2951]">{c}</span>
            </span>
          ))}
        </div>

        <div className="p-5 overflow-x-auto">
          <div className="flex items-end" style={{ minHeight: CHART_H + 80, gap: `${GROUP_GAP}px` }}>
            {rows.map((m) => (
              <div key={m.month} className="flex flex-col items-center" data-testid={`month-${m.label}`}>
                {/* Inner cluster of customer bars */}
                <div className="flex items-end" style={{ height: CHART_H, gap: '2px' }}>
                  {(m.customers || []).length === 0 ? (
                    <div className="w-[40px] h-full bg-[#F1F5F9] rounded-t flex items-center justify-center">
                      <span className="text-[9px] text-[#94A3B8] -rotate-90 whitespace-nowrap">no orders</span>
                    </div>
                  ) : (
                    customers.map(cName => {
                      const c = (m.customers || []).find(x => x.name === cName);
                      const v = c ? c.total_value : 0;
                      const hPx = v > 0 ? Math.max((v / max) * CHART_H, 3) : 0;
                      return (
                        <div
                          key={cName}
                          className="rounded-t transition-all hover:opacity-80"
                          style={{ width: BAR_W, height: hPx, background: v > 0 ? colorOf[cName] : 'transparent' }}
                          title={`${m.label}: ${cName} = ${fmtFull(v)} (${c?.order_count || 0} order${(c?.order_count || 0) !== 1 ? 's' : ''})`}
                        />
                      );
                    })
                  )}
                </div>
                {/* Month label + monthly subtotal */}
                <div className="mt-2 text-center">
                  <div className="text-xs font-bold text-[#4C1D95]">{m.label}</div>
                  <div className="text-[10px] text-[#64748B] font-mono">{m.month_total_value > 0 ? fmtINR(m.month_total_value) : '—'}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Card>
    );
  }

  if (!rows || rows.length === 0) {
    return (
      <Card className="p-10 border border-[#E2E8F0] text-center text-sm text-[#64748B]" data-testid="chart-empty">
        {mode === "customer" && !customer ? "Select a customer to see their month-wise orders." : "No data yet — add sales entries to see customer rankings."}
      </Card>
    );
  }
  const max = Math.max(...rows.map(r => r.total_value || 0), 1);
  const totalAll = rows.reduce((s, r) => s + (r.total_value || 0), 0);
  const fmtINR = (v) => {
    const n = v || 0;
    if (n >= 10000000) return `₹${(n / 10000000).toFixed(2)}Cr`;
    if (n >= 100000)   return `₹${(n / 100000).toFixed(2)}L`;
    if (n >= 1000)     return `₹${(n / 1000).toFixed(1)}K`;
    return `₹${n.toFixed(0)}`;
  };
  const fmtFull = (v) => `₹${(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

  // Vertical bars: each bar represents a customer or month depending on mode.
  const BAR_W = rows.length <= 6 ? 80 : rows.length <= 12 ? 60 : 44;
  const GAP = 16;
  const CHART_H = 320;

  const MONTH_NAMES = ["", "Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const heading = mode === "customer" ? `${customer} — Month-wise Order Value (${year})`
                 : mode === "month" ? `Customers — Order Value for ${MONTH_NAMES[month] || ""} ${year}`
                 : "Customers Ranked by Order Value (All Time)";

  return (
    <Card className="border border-[#E2E8F0] overflow-hidden" data-testid="customer-chart">
      <div className="bg-[#1D2951] px-5 py-3 flex items-center gap-2">
        <ShoppingCart size={18} className="text-[#F5DF4D]" />
        <h2 className="text-sm font-bold text-white uppercase tracking-wider">{heading}</h2>
        <span className="ml-auto text-[11px] text-[#F5DF4D]/80">
          {rows.length} {mode === "customer" ? "months" : "customers"} · Total {fmtFull(totalAll)}
        </span>
      </div>

      <div className="p-5 overflow-x-auto">
        <div className="flex items-end justify-start" style={{ minHeight: CHART_H + 100, gap: `${GAP}px` }}>
          {rows.map((r, idx) => {
            const pct = ((r.total_value || 0) / max) * 100;
            const hPx = Math.max((pct / 100) * CHART_H, 4);
            const key = r.label || r.customer || idx;
            return (
              <div key={key} className="flex flex-col items-center" style={{ width: BAR_W }} data-testid={`bar-${idx}`}>
                {/* Value label above bar */}
                <div className="text-[11px] font-bold text-[#7C3AED] mb-1 whitespace-nowrap">
                  {fmtINR(r.total_value)}
                </div>
                {/* Bar column */}
                <div className="relative w-full bg-[#F1F5F9] rounded-t flex items-end" style={{ height: CHART_H }}>
                  <div
                    className="w-full bg-gradient-to-t from-[#1D2951] to-[#7C3AED] rounded-t transition-all hover:opacity-90"
                    style={{ height: `${hPx}px` }}
                    title={`${key}: ${fmtFull(r.total_value)} · ${r.order_count} order(s)`}
                  />
                  {mode !== "customer" && (
                    <span className="absolute bottom-1 left-1/2 -translate-x-1/2 text-[10px] font-bold text-[#F5DF4D] font-mono">
                      #{idx + 1}
                    </span>
                  )}
                </div>
                {/* Label + order count below bar */}
                <div className="mt-2 w-full text-center">
                  <div className="text-[11px] font-bold text-[#1D2951] truncate" title={key}>
                    {key}
                  </div>
                  <div className="text-[10px] text-[#94A3B8] mt-0.5">
                    {r.order_count} order{r.order_count > 1 ? "s" : ""}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </Card>
  );
}
